import {useCallback, useState} from 'react'
import {Hero, Grid, Box, Stack, Tabs, Image, AnimationProvider, Animate, Text} from '@primer/react-brand-next'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'

import type {FlexSuiteCarouselHeroProps} from './types'
import {getDescription, getHeading} from './utils'

/**
 * Carousel hero: center-aligned hero with tabbed image carousel below
 */
export function FlexSuiteHeroCarousel({fields}: {fields: FlexSuiteCarouselHeroProps}) {
  const {heroHeading, heroLabel, heroDescription, heroCtaOne, heroCtaTwo, heroCarousel, options} = fields

  const [activeTab, setActiveTab] = useState<string>('0')
  const handleTabChange = useCallback((id: string) => {
    // Deferred because Primer Brand's Tabs calls onChange during its render cycle,
    // which would otherwise trigger a React "setState during render" warning.
    queueMicrotask(() => setActiveTab(id))
  }, [])

  return (
    <Box marginBlockEnd={64} borderBlockEndWidth="thin" borderStyle="solid" borderColor="muted">
      <Hero variant={options.variant} align="center">
        {heroLabel && <Hero.Label>{heroLabel}</Hero.Label>}
        <Hero.Heading>{getHeading(heroHeading)}</Hero.Heading>
        <Hero.Description>{heroDescription ? getDescription(heroDescription) : null}</Hero.Description>
        <Hero.PrimaryAction href={heroCtaOne.fields.href}>{heroCtaOne.fields.text}</Hero.PrimaryAction>
        {heroCtaTwo && (
          <Hero.SecondaryAction href={heroCtaTwo.fields.href}>{heroCtaTwo.fields.text}</Hero.SecondaryAction>
        )}
      </Hero>
      {heroCarousel && heroCarousel.length > 0 && (
        <Box borderBlockStartWidth="thin" borderStyle="solid" borderColor="muted">
          <Grid style={{paddingInline: 0}}>
            <Grid.Column>
              <Box borderColor="muted">
                <AnimationProvider autoStaggerChildren={false} animationTrigger="immediate" runOnce>
                  <Stack alignItems="center" padding="none">
                    <Box
                      paddingBlockStart={{narrow: 'condensed', regular: 'spacious'}}
                      paddingInlineEnd="condensed"
                      paddingInlineStart="condensed"
                    >
                      {heroCarousel.map((item, index) => (
                        <Box
                          key={item.sys.id}
                          id={`carousel-panel-image-${index}`}
                          aria-labelledby={`carousel-tab-${index}`}
                          role="tabpanel"
                          tabIndex={0}
                          hidden={activeTab !== String(index)}
                        >
                          <Animate animate="slide-in-up">
                            <Image
                              borderRadius="medium"
                              src={`${item.fields.asset.fields.file.url}?fm=webp`}
                              sources={getImageSources(item.fields.asset.fields.file.url, {
                                maxWidth: MAX_CONTENT_WIDTH,
                                disableDprScaling: true,
                              })}
                              alt={item.fields.asset.fields.description ?? ''}
                            />
                          </Animate>
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </AnimationProvider>
              </Box>
              <Box
                paddingBlockStart={{narrow: 'condensed', regular: 32}}
                paddingBlockEnd={{narrow: 'condensed', regular: 32}}
                paddingInlineStart={{narrow: 'condensed', regular: 48}}
                paddingInlineEnd={{narrow: 'condensed', regular: 48}}
                borderInlineStartWidth={{wide: 'thin'}}
                borderInlineEndWidth={{wide: 'thin'}}
                borderStyle="solid"
                borderColor="muted"
              >
                <Tabs variant="default" onChange={handleTabChange} aria-label="Carousel">
                  {heroCarousel.map((item, index) => (
                    <Tabs.Item
                      key={item.sys.id}
                      id={`carousel-tab-${index}`}
                      aria-controls={`carousel-panel-image-${index} carousel-panel-text-${index}`}
                    >
                      {documentToPlainTextString(item.fields.heading)}
                    </Tabs.Item>
                  ))}
                </Tabs>
                <AnimationProvider autoStaggerChildren={false} animationTrigger="immediate" runOnce>
                  <Box padding="condensed">
                    {heroCarousel.map((item, index) => (
                      <Box
                        key={item.sys.id}
                        id={`carousel-panel-text-${index}`}
                        aria-labelledby={`carousel-tab-${index}`}
                        role="tabpanel"
                        tabIndex={0}
                        hidden={activeTab !== String(index)}
                      >
                        <Animate animate="slide-in-up">
                          <Text as="p" align="center">
                            {documentToPlainTextString(item.fields.description)}
                          </Text>
                        </Animate>
                      </Box>
                    ))}
                  </Box>
                </AnimationProvider>
              </Box>
            </Grid.Column>
          </Grid>
        </Box>
      )}
    </Box>
  )
}
